"use client";

import { useState, useEffect, useMemo } from "react";

import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import Pagination from "../../../../components/Pagination";
import FilterSearch from "../../../../components/FilterSearch(name)";
import FilterButtons from "../../../../components/universal-filter-tabs";

import Checkbox from "../../../../components/Checkbox";
import { PopupProvider, usePopup } from "../context/PopupContext";
import PopupHost from "../context/PopupHost";
import styles from "../css/mergeProfile.module.css";
import { listPatients, getProfileByPatient } from "../../../../api/profile/create_profile";

export default function MergeProfileTable() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");

  const rowsPerPage = 8;

  const handleClosePopup = () => {
    setSelectedRows([]);
  };

  const fetchAllPatients = async () => {
    try {
      const aggregated = [];
      const pageSizeServer = 200;
      let page = 1;
      let total = 0;

      for (;;) {
        const { data, total: serverTotal } = await listPatients({
          search: "",
          page,
          pageSize: pageSizeServer,
        });
        const items = Array.isArray(data) ? data : [];
        aggregated.push(...items);
        total = Number(serverTotal || 0);
        if (!items.length || aggregated.length >= total) break;
        page += 1;
      }

      setTableRows(aggregated);
      setTotalCount(aggregated.length);
    } catch (_) {
      setTableRows([]);
      setTotalCount(0);
    }
  };

  useEffect(() => {
    fetchAllPatients();
  }, [reloadCounter]);

  const filteredRows = useMemo(
    () => {
      const q = (searchTerm || "").toLowerCase().trim();

      const bySearch = (tableRows || []).filter((row) => {
        if (!q) return true;
        const surname = (row.surname || "").toLowerCase();
        const firstname = (row.firstname || "").toLowerCase();
        const initial = (row.initial || "").toLowerCase();
        const title = (row.title || "").toLowerCase();
        const address = (row.address || "").toLowerCase();
        return (
          surname.includes(q) ||
          firstname.includes(q) ||
          initial.includes(q) ||
          title.includes(q) ||
          address.includes(q)
        );
      });

      return bySearch.filter((row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        if (statusFilter === "Active") return enabled;
        if (statusFilter === "Inactive") return !enabled;
        return true;
      });
    },
    [tableRows, searchTerm, statusFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const renderCell = (value, row) => {
    const enabled = row?.profile ? row.profile.enabled !== false : true;
    const style = enabled ? undefined : { color: "#9ca3af" };
    return <span style={style}>{value}</span>;
  };

  const columns = [
    { header: "Surname", accessor: "surname", render: (value, row) => renderCell(value, row) },
    { header: "Initial", accessor: "initial", render: (value, row) => renderCell(value, row) },
    { header: "Title", accessor: "title", render: (value, row) => renderCell(value, row) },
    { header: "Address", accessor: "address", render: (value, row) => renderCell(value, row) },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        const style = enabled ? undefined : { color: "#9ca3af" };
        return <span style={style}>{enabled ? "Enabled" : "Disabled"}</span>;
      },
    },
    {
      header: "Action",
      accessor: "action",
      render: (_, row) => {
        const enabled = row?.profile ? row.profile.enabled !== false : true;
        if (!enabled) {
          return <div className={styles.actionCell} />;
        }

        return (
          <div className={styles.actionCell}>
            <Checkbox
              id={`checkbox-${row.id}`}
              checked={selectedRows.some((r) => r.id === row.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  if (selectedRows.length >= 2) return;
                  setSelectedRows([...selectedRows, row]);
                } else {
                  setSelectedRows(selectedRows.filter((r) => r.id !== row.id));
                }
              }}
            />
          </div>
        );
      },
    },
  ];

  function MergePopupOpener({ selected, shouldOpen }) {
    const { openPopup, closePopup, activePopup } = usePopup();
    useEffect(() => {
      async function openIfReady() {
        if (!shouldOpen || activePopup) return;
        if (!Array.isArray(selected) || selected.length !== 2) return;
        const [a, b] = selected;
        try {
          const [profileA, profileB] = await Promise.all([
            getProfileByPatient(a.id),
            getProfileByPatient(b.id),
          ]);

          const mapProfile = (src, full) => {
            const main = full?.main || {};
            const mm = full?.mainmember || {};
            const prof = full?.profile || {};
            const bens = Array.isArray(full?.beneficiaries) ? full.beneficiaries : [];
            const practitioner = mm?.practitioner || {};

            const doctorName = `${practitioner.title || ""} ${practitioner.firstname || ""} ${practitioner.surname || ""}`.trim();
            const practiceNumber = practitioner.practicenumber || "";

            return {
              patientId: src.id,
              profileNumber: prof.profilenumber || "",
              name: `${main.title || src.title || ""} ${main.surname || src.surname || ""} ${src.initial || ""}`.trim(),
              idNumber: main.idnumber || "",
              email: main.email || "",
              sex: main.sex || "",
              phone: main.contactnumber || "",
              address: main.homeaddress || src.address || "",
              chronicConditions: Array.isArray(main.chronicconditions) ? main.chronicconditions.join(", ") : "",
              beneficiaries: bens.length,
              medicalAidProvider: mm.medicalaidprovider || "",
              medicalAidNumber: mm.medicalaidnumber || "",
              doctor: doctorName,
              practiceNumber,
              isMainMember: true,
            };
          };

          const popupProfiles = [
            mapProfile(a, profileA),
            mapProfile(b, profileB),
          ];

          openPopup("mergeProfiles", { profiles: popupProfiles });
        } catch (_) {
          closePopup();
        }
      }
      openIfReady();
      if (!shouldOpen && activePopup === "mergeProfiles") {
        closePopup();
      }
    }, [shouldOpen, selected, activePopup, openPopup, closePopup]);
    return null;
  }

  function PopupHostWrapper() {
    const { closePopup } = usePopup();

    const handleClose = () => {
      closePopup();
      handleClosePopup();
      setReloadCounter((v) => v + 1);
    };

    return <PopupHost onCloseAll={handleClose} />;
  }

  return (
    <PopupProvider>
      <div className={styles.app}>
        <div className={styles.mainContainer}>
          <Navbar />
          <main className={styles.content}>
            <div className={styles.filterBar}>
              <div className={styles.filterCard}>
                <FilterSearch
                  filterName="Filter"
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search by surname, initial, title, or address..."
                />
                <FilterButtons
                  buttons={["All", "Active", "Inactive"]}
                  activeButton={statusFilter}
                  onButtonClick={setStatusFilter}
                />
              </div>
            </div>

            <Table columns={columns} data={paginatedData} />
            {totalPages > 1 && (
              <div className={styles.paginationContainer}>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            )}
            <MergePopupOpener selected={selectedRows} shouldOpen={selectedRows.length === 2} />
          </main>
          <PopupHostWrapper />
          <div className={styles.footerWrapper}>
            <Footer />
          </div>
        </div>
      </div>
    </PopupProvider>
  );
}